import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ModalConfirmacao from '../../../components/modal/confirmacao/ModalConfirmacao';
import {
  Botao,
  Chip,
  ColunaDataList,
  DataList,
  EmptyState,
  ErrorState,
  Pagination,
  SearchInput,
  Skeleton,
} from '../../../ui';

import {
  ErroRascunhoAdesao,
  FiltroStatusRascunho,
  ListaRascunhosAdesao,
  RascunhoAdesaoResumo,
  descartarRascunho,
  listarRascunhos,
  obterRascunho,
} from '../../../api/rascunho-adesao/rascunhoAdesaoApi';
import {
  hidratarFluxoAdesao,
  rotaDaEtapa,
  rotuloEtapa,
  rotuloStatus,
} from '../../../utils/adesaoFluxo';
import { formatCpfCnpj } from '../../../utils/formartCPF';

/**
 * Aba "Em andamento" de `/adesoes` — os rascunhos de adesão do consultor
 * (at-38; reescrita sobre o design system na at-56, frente B).
 *
 * A adesão é um fluxo de 6 telas. Se o consultor é interrompido (logout, aparelho
 * trocado, navegador limpo), o que ele digitou vive num rascunho no servidor.
 * Esta aba é a **fila de trabalho parado**.
 *
 * Só o próprio consultor vê os próprios rascunhos: o escopo é do backend
 * (`consultorId` do JWT) — a tela não filtra nada por conta própria.
 *
 * ## O que a reescrita mudou
 *
 *   • **Uma lista, não duas.** O arquivo renderizava uma `<table>` E um bloco de
 *     cards ao mesmo tempo, com o CSS escondendo um por breakpoint — o texto de
 *     vazio e de "Carregando..." aparecia duas vezes, e `chipsDoItem`/`acoesDoItem`
 *     existiam só para não triplicar o JSX. Agora é uma definição de colunas no
 *     `DataList`.
 *   • **Erro oferece "tentar novamente"** (spec 02 §3.8) — era só um toast, com a
 *     lista vazia e nenhuma saída.
 *   • **Vazio-por-busca ≠ fila vazia**, e na fila vazia o texto diz que isso é
 *     BOA notícia (§3.8): não há trabalho parado.
 *   • Carregamento virou `Skeleton` no lugar da lista.
 *
 * ## O que NÃO mudou (contratos da spec 02 §8)
 *
 * `Continuar` limpa → aplica `payload` → grava ids → navega pela tabela
 * etapa→rota; `409` é "encerrado em outro dispositivo" (informativo, não erro);
 * o chip de etapa é a **próxima** tela, não a concluída; e descartar **marca**,
 * nunca apaga.
 */

const LIMITE = 10;

interface Props {
  /** Alimenta o contador "Em andamento (N)" do grupo de abas. */
  onTotalEmAndamento?: (total: number) => void;
}

const RascunhosAdesaoTab: React.FC<Props> = ({ onTotalEmAndamento }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [dados, setDados] = useState<ListaRascunhosAdesao | null>(null);
  const [page, setPage] = useState(1);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [busca, setBusca] = useState('');

  const [aDescartar, setADescartar] = useState<RascunhoAdesaoResumo | null>(null);
  const [descartando, setDescartando] = useState(false);
  const [continuandoId, setContinuandoId] = useState<string | null>(null);
  /** Rascunho cujo CPF já tem associado cadastrado — precisa de confirmação. */
  const [avisoCpfCadastrado, setAvisoCpfCadastrado] = useState<{
    id: string;
    nome: string;
  } | null>(null);

  const carregar = useCallback(
    async (pagina: number, termo: string, historico: boolean) => {
      setCarregando(true);
      setErro('');
      try {
        const status: FiltroStatusRascunho = historico ? 'TODOS' : 'EM_ANDAMENTO';
        const resposta = await listarRascunhos({
          page: pagina,
          limit: LIMITE,
          busca: termo,
          status,
        });
        setDados(resposta);
        onTotalEmAndamento?.(resposta.totais?.emAndamento ?? 0);
      } catch (excecao) {
        setDados(null);
        setErro((excecao as Error).message);
      } finally {
        setCarregando(false);
      }
    },
    [onTotalEmAndamento],
  );

  useEffect(() => {
    void carregar(page, busca, mostrarHistorico);
  }, [page, busca, mostrarHistorico, carregar]);

  const trocarHistorico = (valor: boolean) => {
    setMostrarHistorico(valor);
    setPage(1);
  };

  const trocarBusca = (termo: string) => {
    setBusca(termo);
    setPage(1);
  };

  /** Reidrata o Redux a partir do banco e abre a tela da etapa pendente. */
  const abrirRascunho = async (id: string, ignorarAvisoCpf = false) => {
    setContinuandoId(id);
    try {
      const rascunho = await obterRascunho(id);

      if (rascunho.cpfJaCadastrado && !ignorarAvisoCpf) {
        setAvisoCpfCadastrado({ id, nome: rascunho.nomeCompleto });
        return;
      }

      if (!rascunho.payload) {
        // Status terminal zera o payload — sem snapshot não há o que retomar.
        toast.info('Este rascunho já foi encerrado e não pode mais ser retomado.');
        await carregar(page, busca, mostrarHistorico);
        return;
      }

      hidratarFluxoAdesao(dispatch, rascunho);

      const destino = rotaDaEtapa(rascunho);
      navigate(destino.rota, destino.state ? { state: destino.state } : undefined);
    } catch (excecao) {
      const status = (excecao as ErroRascunhoAdesao)?.status ?? null;
      if (status === 404 || status === 409) {
        toast.info('Este rascunho não está mais disponível.');
      } else {
        toast.error((excecao as Error).message);
      }
      await carregar(page, busca, mostrarHistorico);
    } finally {
      setContinuandoId(null);
    }
  };

  const confirmarDescarte = async () => {
    if (!aDescartar) return;

    setDescartando(true);
    try {
      await descartarRascunho(aDescartar.id);
      toast.success('Rascunho descartado.');
      setADescartar(null);
      await carregar(page, busca, mostrarHistorico);
    } catch (excecao) {
      const status = (excecao as ErroRascunhoAdesao)?.status ?? null;
      toast.error(
        status === 409
          ? 'Este rascunho já foi encerrado em outro dispositivo.'
          : (excecao as Error).message,
      );
      setADescartar(null);
      await carregar(page, busca, mostrarHistorico);
    } finally {
      setDescartando(false);
    }
  };

  const itens = dados?.items ?? [];
  const totalItems = dados?.totalItems ?? 0;
  const ocupado = carregando || descartando || continuandoId !== null;

  const colunas: ColunaDataList<RascunhoAdesaoResumo>[] = [
    {
      id: 'associado',
      rotulo: 'Associado',
      principal: true,
      celula: (item) => item.nomeCompleto || '—',
    },
    {
      id: 'cpf',
      rotulo: 'CPF/CNPJ',
      secundaria: true,
      celula: (item) => (
        <span className="tw-tabular-nums">{formatCpfCnpj(item.cpfCnpj || '')}</span>
      ),
    },
    {
      id: 'placa',
      rotulo: 'Placa',
      secundaria: true,
      // Rascunho parado na primeira etapa ainda não tem veículo.
      celula: (item) => item.placa || '—',
    },
    {
      id: 'situacao',
      rotulo: 'Situação',
      celula: (item) => {
        const status = rotuloStatus(item.status);
        return (
          <span className="tw-inline-flex tw-flex-wrap tw-gap-1.5">
            <Chip variante={status.variante}>{status.texto}</Chip>
            {item.status === 'EM_ANDAMENTO' && (
              // A etapa é a PRÓXIMA tela a abrir, ou seja, o que FALTA fazer —
              // não o que já foi feito (contrato §8.8).
              <Chip variante="marca" title="Próxima tela a abrir na retomada">
                {rotuloEtapa(item.etapa)}
              </Chip>
            )}
          </span>
        );
      },
    },
    {
      id: 'atualizado',
      rotulo: 'Atualizado',
      celula: (item) => {
        const iso = dataDeReferencia(item);
        return (
          <span title={dataCompleta(iso)} className="tw-whitespace-nowrap">
            {tempoRelativo(iso)}
          </span>
        );
      },
    },
  ];

  const acoesDoItem = (item: RascunhoAdesaoResumo) => {
    // Só `EM_ANDAMENTO` é operável; nos terminais não há ação.
    if (item.status !== 'EM_ANDAMENTO') {
      return <span className="tw-text-xs tw-text-texto-desabilitado">—</span>;
    }

    return (
      // ⚠️ Duas exigências da spec §9 aqui, e as duas por causa do "Descartar",
      // que é destrutivo mesmo com confirmação: alvo de toque de **44px**
      // (`tamanho="md"`, não `sm`) e folga suficiente para ele não ser tocado no
      // lugar do "Continuar" — daí o `gap-3` em vez do `gap-2` do resto da tela.
      <div className="tw-flex tw-flex-wrap tw-justify-start tw-gap-3">
        <Botao
          variante="primario"
          tamanho="md"
          disabled={ocupado}
          carregando={continuandoId === item.id}
          onClick={() => void abrirRascunho(item.id)}
        >
          {continuandoId === item.id ? 'Abrindo...' : 'Continuar'}
        </Botao>
        <Botao
          variante="secundario"
          tamanho="md"
          disabled={ocupado}
          onClick={() => setADescartar(item)}
        >
          Descartar
        </Botao>
      </div>
    );
  };

  const conteudo = () => {
    if (carregando) return <Skeleton linhas={3} />;

    if (erro) {
      return (
        <ErrorState
          mensagem="Não foi possível carregar as adesões em andamento."
          detalhe={erro}
          aoTentarNovamente={() => void carregar(page, busca, mostrarHistorico)}
        />
      );
    }

    if (!itens.length) {
      return busca ? (
        <EmptyState
          titulo="Nenhum resultado para essa busca"
          descricao="Confira o termo digitado — a busca casa nome, CPF e placa."
          icone="busca"
        />
      ) : (
        <EmptyState
          titulo="Nenhuma adesão em andamento"
          // Aqui vazio é BOA notícia, e a tela deve dizer (§3.8).
          descricao={
            mostrarHistorico
              ? 'Você ainda não tem rascunhos no histórico.'
              : 'Nada parado pela metade — todas as suas adesões foram concluídas ou descartadas.'
          }
        />
      );
    }

    return (
      <DataList
        itens={itens}
        colunas={colunas}
        chave={(item) => item.id}
        acoes={acoesDoItem}
        rotuloAcessivel="Adesões em andamento"
      />
    );
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/*
        Ordem do protótipo: controles primeiro, legenda depois.

        🔴 **GRID de duas colunas, não `flex` + `flex-wrap`.** O checkbox tem de
        ficar **ao lado** do campo (Victor, 09/09/2026), e com flex-wrap ele
        insistia em cair para a linha de baixo. Aqui as duas faixas são
        declaradas: `1fr auto` — o campo fica com o espaço que sobra e o rótulo
        com o tamanho de que precisa, **sem depender de cálculo de wrap**.
        No celular é uma coluna só (`grid-cols-1`), que empilha de propósito.
      */}
      <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-[1fr_auto] sm:tw-items-center">
        <SearchInput
          aoBuscar={trocarBusca}
          placeholder="Buscar por nome, CPF ou placa"
          rotuloAcessivel="Buscar rascunhos por nome, CPF ou placa"
          disabled={ocupado && !carregando}
          className="tw-min-w-0"
        />

        {/* `whitespace-nowrap` para o rótulo não quebrar em duas linhas dentro da
            própria coluna — a coluna `auto` já reserva a largura dele. */}
        <label className="tw-inline-flex tw-cursor-pointer tw-select-none tw-items-center tw-gap-2 tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-texto-corpo">
          <input
            type="checkbox"
            checked={mostrarHistorico}
            disabled={ocupado}
            onChange={(evento) => trocarHistorico(evento.target.checked)}
            /* ⚠️ `accent-brand` é o que pinta o marcador com a cor da marca. Sem
               ele o checkbox herda o azul do sistema — ou, pior, o estilo global
               `input[type="checkbox"]` que vive em `views/produto/pages/produto.css`
               e vale para o app inteiro. */
            className="tw-h-4 tw-w-4 tw-cursor-pointer tw-rounded tw-accent-brand"
          />
          Mostrar histórico
        </label>

        {/*
          ⚠️ **O chip "N em andamento" SAIU** (Victor, 09/09/2026). A spec 02 §3.4
          o pedia, mas ele é **redundante**: o mesmo número já aparece no rótulo da
          aba ("Em andamento (2)"), que é onde o consultor o procura. Repetir o
          contador logo abaixo só ocupava uma linha da tela.
          📌 A regra que ele carregava — o total é GLOBAL do consultor e não segue
          a busca (convenção at-10/at-28) — continua valendo no contador da aba.
        */}
      </div>

      {/* A regra fica dita na tela (convenção do projeto), inclusive o que o
          rascunho NÃO guarda — senão o consultor descobre no meio da retomada.
          Caixa cinza, como no protótipo (era faixa com borda laranja à esquerda,
          que competia em ênfase com o conteúdo). */}
      <p className="tw-m-0 tw-rounded-lg tw-bg-superficie tw-px-3 tw-py-2 tw-text-xs tw-leading-relaxed tw-text-texto-suave">
        <strong className="tw-text-texto-corpo">Atenção:</strong> rascunhos guardam os dados
        digitados até a conclusão da adesão. Fotos de documentos não são guardadas e serão
        recapturadas.
      </p>

      {conteudo()}

      {!carregando && !erro && itens.length > 0 && (
        <Pagination
          pagina={page}
          totalItens={totalItems}
          porPagina={LIMITE}
          aoTrocar={setPage}
          ocupado={ocupado}
        />
      )}

      <ModalConfirmacao
        aberto={aDescartar !== null}
        titulo="Descartar rascunho"
        mensagem={`Descartar a adesão em andamento de ${aDescartar?.nomeCompleto ?? ''}?`}
        detalhe="Os dados digitados deixam de estar disponíveis para retomada. O registro fica no histórico."
        rotuloConfirmar="Descartar"
        destrutivo
        processando={descartando}
        onConfirmar={() => void confirmarDescarte()}
        onCancelar={() => setADescartar(null)}
      />

      <ModalConfirmacao
        aberto={avisoCpfCadastrado !== null}
        titulo="CPF já cadastrado"
        mensagem={`Já existe um associado com o CPF de ${
          avisoCpfCadastrado?.nome ?? ''
        } no SAAC.`}
        detalhe="O cadastro pode ter sido feito por fora deste rascunho. Continuar vai reabrir o fluxo com os dados digitados — se preferir, cancele e use Descartar na lista."
        rotuloConfirmar="Continuar assim mesmo"
        processando={continuandoId !== null}
        onConfirmar={() => {
          const id = avisoCpfCadastrado?.id;
          setAvisoCpfCadastrado(null);
          if (id) void abrirRascunho(id, true);
        }}
        onCancelar={() => {
          setAvisoCpfCadastrado(null);
          setContinuandoId(null);
        }}
      />
    </div>
  );
};

/** Data do rascunho, preferindo a última atualização. */
const dataDeReferencia = (item: RascunhoAdesaoResumo): string | null =>
  item.atualizadoEm ?? item.concluidoEm ?? item.descartadoEm ?? item.criadoEm ?? null;

const dataCompleta = (iso: string | null): string => {
  if (!iso) return '';
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
};

/** "hoje" / "há N dias" — mais legível que a data crua numa fila de trabalho. */
const tempoRelativo = (iso: string | null): string => {
  if (!iso) return '—';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '—';

  const dias = Math.floor((Date.now() - data.getTime()) / 86_400_000);
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'há 1 dia';
  return `há ${dias} dias`;
};

export default RascunhosAdesaoTab;
